import { api } from "api/root";
import { Dispatch } from "react";
import { handleRequestErrors } from "helpers/functions/commons";
import { addNotification } from "store/notifications/slice";

export const R_addGridDataView = async (data: {workspaceId: string, type: string, gridColumns?: string[], datasetId: null | string, title: string | null}, thunkAPI: unknown) => {
    try {
        const response = await api.post(`/Workspace/${data.workspaceId}/GridView`, {identifierFieldType: +data.type, gridColumns: data.gridColumns, datasetId: data.datasetId, title: data.title});
        return {...response.data};
    } catch (e: unknown) {
        const validationMessage = handleRequestErrors(e)
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const thunkRejectError = thunkAPI as { rejectWithValue: (message: any) => any, dispatch: Dispatch<unknown> }
        thunkRejectError.dispatch(addNotification({ type: 'error', message: validationMessage }));
        return thunkRejectError.rejectWithValue(validationMessage);
    }
}

export const R_addGridDataViewData = async (data: {workspaceId: string, plotId: string, offset: number, limit: number, showImputedData?: boolean}, thunkAPI: unknown) => {
    try {
        const response = await api.get(`/Workspace/${data.workspaceId}/GridView/${data.plotId}/Data`, {params: {offset: +data.offset, limit: data.limit, showImputedData: data.showImputedData }});
        return {...response.data};
    } catch (e: unknown) {
        const validationMessage = handleRequestErrors(e)
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const thunkRejectError = thunkAPI as { rejectWithValue: (message: any) => any, dispatch: Dispatch<unknown> }
        thunkRejectError?.dispatch(addNotification({ type: 'error', message: validationMessage }));
        return thunkRejectError ? thunkRejectError.rejectWithValue(validationMessage) : e;
    }
}

export const R_addGridDataResultViewData = async (data: {workspaceId: string, plotId: string, offset: number, limit: number}, thunkAPI: unknown) => {
    try {
        const response = await api.post(`/Workspace/${data.workspaceId}/GridViewResult/${data.plotId}/Data`, {params: {offset: +data.offset, limit: data.limit }}, {});
        return {...response.data};
    } catch (e: unknown) {
        const validationMessage = handleRequestErrors(e)
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const thunkRejectError = thunkAPI as { rejectWithValue: (message: any) => any, dispatch: Dispatch<unknown> }
        thunkRejectError?.dispatch(addNotification({ type: 'error', message: validationMessage }));
        return thunkRejectError ? thunkRejectError.rejectWithValue(validationMessage) : e;
    }
}

export const R_addGridDataResultViewDataAnalogue = async (data: {
  workspaceId: string, 
  plotId: string, 
  offset: number, 
  limit: number,
  broadParameterTagAttributeIds?: string[],
  scoringParameterTagAttributeIds?: string[],
  filterOptions?: any[]
}, thunkAPI: unknown) => {
    try {
        const requestBody: any = {};
        
        // Add tagAttributeIds if provided
        if (data.broadParameterTagAttributeIds && data.broadParameterTagAttributeIds.length > 0) {
            requestBody.broadParameterTagAttributeIds = data.broadParameterTagAttributeIds;
        }
        
        if (data.scoringParameterTagAttributeIds && data.scoringParameterTagAttributeIds.length > 0) {
            requestBody.scoringParameterTagAttributeIds = data.scoringParameterTagAttributeIds;
        }
        
        // Add filter options if provided
        if (data.filterOptions && data.filterOptions.length > 0) {
            requestBody.filterOptions = data.filterOptions;
        }
        requestBody.workspaceId = data.workspaceId;
        
        const response = await api.post(
            `/AnalogueWorkSpace/GridViewResult/Data`, 
            requestBody,
            {params: {offset: +data.offset, limit: data.limit }}
        );
        return {...response.data};
    } catch (e: unknown) {
        const validationMessage = handleRequestErrors(e)
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const thunkRejectError = thunkAPI as { rejectWithValue: (message: any) => any, dispatch: Dispatch<unknown> }
        thunkRejectError?.dispatch(addNotification({ type: 'error', message: validationMessage }));
        return thunkRejectError ? thunkRejectError.rejectWithValue(validationMessage) : e;
    }
}

// Interface for scoring parameter details
export interface IScoringParameterDetail {
    tagAttributeId: string;
    min: number;
    max: number;
    xAxis: boolean;
    yAxis: boolean;
}

// New API function for analogue plotting data with scoring parameter filtering
export const R_getAnalogueePlottingData = async (data: { 
    workspaceId: string, 
    plotId: string, 
    plotType: 'crossplot' | 'histogram',
    xTagAttributeId?: string,
    yTagAttributeId?: string,
    broadParameterTagAttributeIds?: string[],
    scoringParameters?: IScoringParameterDetail[],
    filterOptions?: any[]
}, thunkAPI: unknown) => {
    try {
        const response = await api.post(`/Workspace/${data.workspaceId}/AnaloguePlot/${data.plotId}/PlottingData`, {
            plotType: data.plotType,
            xTagAttributeId: data.xTagAttributeId,
            yTagAttributeId: data.yTagAttributeId,
            broadParameterTagAttributeIds: data.broadParameterTagAttributeIds || [],
            scoringParameters: data.scoringParameters || [],
            filterOptions: data.filterOptions || []
        });
        return response.data;
    } catch (e: unknown) {
        const validationMessage = handleRequestErrors(e)
        const thunkRejectError = thunkAPI as { rejectWithValue: (message: any) => any }
        return thunkRejectError.rejectWithValue(validationMessage);
    }
}


export const R_changeGridDataView = async (data: {workspaceId: string, plotId: string, tagAttributeId: string, isFreezed?: boolean, order?: number}, thunkAPI: unknown) => {
    try {
        const response = await api.patch(`/Workspace/${data.workspaceId}/GridView/${data.plotId}/Columns`, {
            tagAttributeId: data?.tagAttributeId,
            isFreezed: data?.isFreezed,
            order: data?.order,
        });
        return {...response.data};
    } catch (e: unknown) {
        const validationMessage = handleRequestErrors(e)
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const thunkRejectError = thunkAPI as { rejectWithValue: (message: any) => any, dispatch: Dispatch<unknown> }
        thunkRejectError?.dispatch(addNotification({ type: 'error', message: validationMessage }));
        return thunkRejectError?.rejectWithValue(validationMessage) || {errorMsg: validationMessage};
    }
}

export const R_changeGridDataViewOrder = async (data: {workspaceId: string, plotId: string, gridColumns: {tagAttributeId: string, isFreezed?: boolean, order?: number}}, thunkAPI: unknown) => {
    try {
        const response = await api.put(`/Workspace/${data.workspaceId}/GridView/${data.plotId}`, { gridColumns: data.gridColumns });
        return {...response.data};
    } catch (e: unknown) {
        const validationMessage = handleRequestErrors(e)
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const thunkRejectError = thunkAPI as { rejectWithValue: (message: any) => any, dispatch: Dispatch<unknown> }
        thunkRejectError?.dispatch(addNotification({ type: 'error', message: validationMessage }));
        return thunkRejectError?.rejectWithValue(validationMessage) || {errorMsg: validationMessage};
    }
}